import React, { useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { STATUSES } from 'constants/common';

import * as api from 'api/methods';

import { currentCourseSelector } from 'store/course/selectors';
import { currentLessonSelector } from 'store/lesson/selectors';

import { showToast } from 'helpers/showToast';
import { getLessonPreviewPathByCourseType } from 'helpers/getLessonPathByCourseType';
import { getTranslation } from 'helpers/getTranslation';
import { isLessonReadOnly } from 'helpers/isLessonReadOnly';

import { useStatusChange } from 'hooks/useStatusChange';
import { useLocalizedLessonSaveHandlers } from 'hooks/useLocalizedLessonSaveHandlers';

import {
  convertDataToApi,
  checkAnswersValue,
  checkCorrectAnswers,
  getChangedProperties,
} from './QuizPage.helpers';

import QuizPage from './QuizPage';

const QuizPageContainer = (props: any) => {
  const lesson = useAppSelector(currentLessonSelector);

  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [lastActionAt, setLastActionAt] = useState(lesson.updatedAt);

  const navigate = useNavigate();

  const { courseId, lessonId } = useParams();

  const course = useAppSelector(currentCourseSelector);

  const [onStatusContextItemClick] = useStatusChange(lesson);

  const {
    activeLocale,
    availableLocales,
    baseLesson,
    editorLesson,
    localeContentLoading,
    localesWithContent,
    mergedLessonLoading,
    onLocaleChange,
    handleAutoSaveSubmit,
    handleSaveClick,
  } = useLocalizedLessonSaveHandlers({
    lessonId: lessonId as string,
    lessonType: lesson.type,
    baseLesson: lesson,
    convertDataToApi,
    getChangedProperties,
  });

  const handleStatusContextItemClick = (
    contextItem: any,
    values: any,
    { setFieldValue }: { setFieldValue: any },
  ) => {
    const setValidationMessage = (message: any) => {
      setFieldValue('statusError', message);

      showToast({
        data: {
          variant: 'warning',
          title: getTranslation('quiz_page.validation_message.title'),
          text: message,
        },
      });
    };

    if (contextItem.key === STATUSES.PUBLISHED) {
      if (!checkCorrectAnswers(values)) {
        setValidationMessage(
          getTranslation('quiz_page.validation_message.marked'),
        );

        return;
      }

      if (!checkAnswersValue(values)) {
        setValidationMessage(
          getTranslation('quiz_page.validation_message.contain'),
        );

        return;
      }
    }

    onStatusContextItemClick(contextItem, convertDataToApi(values));
  };

  const onAutoSaveSubmit = async (values: any) => {
    setAutoSaveLoading(true);

    const updatedAt = await handleAutoSaveSubmit(values);

    if (updatedAt) {
      setLastActionAt(updatedAt);
    }

    setAutoSaveLoading(false);
  };

  const onSaveClick = async (values: any) => {
    setSaveLoading(true);

    try {
      const updatedAt = await handleSaveClick(values);
      setLastActionAt(updatedAt);

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation('common.toast.lesson_saved.text'),
        },
      });
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.error.text'),
        },
      });
    }

    setSaveLoading(false);
  };

  const handlePreviewClick = () => {
    navigate(
      getLessonPreviewPathByCourseType({
        courseType: course.type,
        courseId,
        lessonId,
      }),
    );
  };

  const lessonReadOnly = isLessonReadOnly(lesson.status);

  return (
    <QuizPage
      {...props}
      saveLoading={saveLoading}
      autoSaveLoading={autoSaveLoading || mergedLessonLoading}
      readOnly={lessonReadOnly}
      baseLesson={baseLesson}
      lesson={editorLesson}
      localeContentLoading={localeContentLoading}
      lastActionAt={lastActionAt}
      courseTitle={course.title || getTranslation('common.text.untitled')}
      courseStatus={course.status}
      isCatalogLesson={!courseId}
      activeLocale={activeLocale}
      availableLocales={availableLocales}
      localesWithContent={localesWithContent}
      lessonType={lesson.type}
      onLocaleChange={onLocaleChange}
      onAutoSaveSubmit={onAutoSaveSubmit}
      onSaveClick={onSaveClick}
      onPreviewClick={handlePreviewClick}
      onStatusContextItemClick={handleStatusContextItemClick}
    />
  );
};

export default React.memo(QuizPageContainer) as any;
